import React, { useState, useEffect, useCallback } from 'react';
import Navigation from './Navigation';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import Spinner from './Spinner';
import Toast from './Toast';
import DealerSmsLists from './DealerSmsLists';

// Admin module: list of car dealerships (avtohiše) scraped from avto.net.
function DealersAdminPanel() {
  const { user } = useAuth();
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  const [status, setStatus] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [expandedNotes, setExpandedNotes] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [noteStatus, setNoteStatus] = useState({});

  const [proxySource, setProxySource] = useState('shared');
  const [dealerProxies, setDealerProxies] = useState([]);
  const [sharedEnabledCount, setSharedEnabledCount] = useState(0);
  const [newProxyHost, setNewProxyHost] = useState('');
  const [newProxyPort, setNewProxyPort] = useState('3128');
  const [newProxyUser, setNewProxyUser] = useState('');
  const [newProxyPass, setNewProxyPass] = useState('');

  // SMS avtohisam: izbira, liste, izvoz
  const [selected, setSelected] = useState(() => new Set());
  const [smsLists, setSmsLists] = useState([]);
  const [defaultListName, setDefaultListName] = useState('');
  const [targetList, setTargetList] = useState('new');
  const [newListName, setNewListName] = useState('');
  const [listsReload, setListsReload] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const [manualText, setManualText] = useState('');

  const showError = (e, fallback) => setToast({ type: 'error', message: (e && e.response && e.response.data && e.response.data.error) || fallback });
  const showOk = (message) => setToast({ type: 'success', message });

  const loadStatus = useCallback(async () => {
    try {
      const res = await apiService.getDealerStatus();
      setStatus(res.data);
    } catch (e) { /* handled by forbidden */ }
  }, []);

  const loadDealers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getDealers(page, 50, query);
      setDealers(res.data.dealers || []);
      setTotalPages(res.data.totalPages || 1);
      setTotalElements(res.data.totalElements || 0);
    } catch (e) {
      if (e.response && e.response.status === 403) setForbidden(true);
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  const loadProxies = useCallback(async () => {
    try {
      const res = await apiService.getDealerProxies();
      setProxySource(res.data.source || 'shared');
      setDealerProxies(res.data.proxies || []);
      setSharedEnabledCount(res.data.sharedEnabledCount || 0);
    } catch (e) { /* handled by forbidden */ }
  }, []);

  const loadSmsLists = useCallback(async () => {
    try {
      const res = await apiService.getDealerSmsLists();
      setSmsLists(res.data.lists || []);
      setDefaultListName(res.data.defaultName || '');
    } catch (e) { /* handled by forbidden */ }
  }, []);

  useEffect(() => { loadDealers(); }, [loadDealers]);
  useEffect(() => { loadSmsLists(); }, [loadSmsLists, listsReload]);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadProxies(); }, [loadProxies]);

  const changeSource = async (source) => {
    setProxySource(source);
    try {
      await apiService.setDealerProxySource(source);
      showOk(source === 'dedicated' ? 'Vir: namenski proxyji' : 'Vir: deljeni proxyji');
      loadProxies();
    } catch (e) {
      showError(e, 'Napaka pri shranjevanju vira');
    }
  };

  const addProxy = async () => {
    if (!newProxyHost.trim()) return;
    try {
      await apiService.addDealerProxy(newProxyHost.trim(), parseInt(newProxyPort, 10) || 3128, newProxyUser.trim(), newProxyPass);
      setNewProxyHost('');
      setNewProxyPort('3128');
      setNewProxyUser('');
      setNewProxyPass('');
      loadProxies();
    } catch (e) {
      showError(e, 'Napaka pri dodajanju proxyja');
    }
  };

  const removeProxy = async (id) => {
    if (!window.confirm('Odstraniti ta proxy?')) return;
    try {
      await apiService.deleteDealerProxy(id);
      loadProxies();
    } catch (e) {
      showError(e, 'Napaka pri brisanju proxyja');
    }
  };

  const toggleProxy = async (id) => {
    try {
      await apiService.toggleDealerProxy(id);
      loadProxies();
    } catch (e) {
      showError(e, 'Napaka pri preklopu proxyja');
    }
  };

  const doSearch = () => {
    setPage(0);
    setQuery(search.trim());
  };

  const requestScan = async () => {
    setScanning(true);
    try {
      const res = await apiService.scanDealers();
      setStatus(res.data);
      showOk('Scan zahtevan – scraper bo poiskal nove avtohiše.');
    } catch (e) {
      showError(e, 'Napaka pri zagonu scana');
    } finally {
      setScanning(false);
    }
  };

  const deleteDealer = async (id, name) => {
    if (!window.confirm(`Trajno izbrisati avtohišo "${name || id}"?`)) return;
    try {
      await apiService.deleteDealer(id);
      setDealers((list) => list.filter((d) => d.id !== id));
      showOk('Avtohiša izbrisana');
    } catch (e) {
      showError(e, 'Napaka pri brisanju');
    }
  };

  const cleanupEmpty = async () => {
    if (!window.confirm('Izbrisati vse prazne (www.Avto.net) avtohiše, razen najvišje?')) return;
    setCleaning(true);
    try {
      const res = await apiService.cleanupEmptyDealers();
      showOk(`Izbrisanih praznih: ${res.data.deleted}`);
      loadDealers();
    } catch (e) {
      showError(e, 'Napaka pri čiščenju');
    } finally {
      setCleaning(false);
    }
  };

  const toggleNotes = (d) => {
    if (expandedNotes === d.id) { setExpandedNotes(null); return; }
    setExpandedNotes(d.id);
    setNoteDrafts((n) => ({ ...n, [d.id]: d.notes || '' }));
  };

  const saveNotes = async (id) => {
    setNoteStatus((s) => ({ ...s, [id]: 'Shranjujem ...' }));
    try {
      await apiService.updateDealerNotes(id, noteDrafts[id] || '');
      setDealers((list) => list.map((d) => (d.id === id ? { ...d, notes: noteDrafts[id] || '' } : d)));
      setNoteStatus((s) => ({ ...s, [id]: '✓ Shranjeno' }));
    } catch (e) {
      setNoteStatus((s) => ({ ...s, [id]: '✗ Napaka' }));
    }
    setTimeout(() => setNoteStatus((s) => ({ ...s, [id]: '' })), 2500);
  };

  const toggleSelected = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const selectPage = (onlySendable) => setSelected((prev) => {
    const n = new Set(prev);
    dealers.forEach((d) => { if (!onlySendable || d.smsLahko) n.add(d.id); });
    return n;
  });

  const addToList = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      if (targetList === 'new') {
        const res = await apiService.createDealerSmsList(newListName.trim() || defaultListName, ids);
        showOk(`Lista "${res.data.name}" ustvarjena, dodanih ${res.data.added}.`);
      } else {
        const res = await apiService.addToDealerSmsList(targetList, ids);
        showOk(`Dodanih v listo: ${res.data.added}`);
      }
      setSelected(new Set());
      setNewListName('');
      setListsReload((k) => k + 1);
    } catch (e) {
      showError(e, 'Napaka pri dodajanju v listo');
    }
  };

  const setFlag = async (d, field, value) => {
    try {
      await apiService.updateDealerFlags(d.id, { [field]: value });
      loadDealers();
      setListsReload((k) => k + 1);
    } catch (e) {
      showError(e, 'Napaka pri shranjevanju oznake');
    }
  };

  const addManual = async () => {
    if (!manualText.trim()) return;
    try {
      const res = await apiService.addManualBrokers(manualText);
      showOk(`V vrsto dodanih: ${res.data.added} (brokerji ${(res.data.brokers || []).join(', ')}). Skener jih obdela prve.`);
      setManualText('');
      loadStatus();
    } catch (e) {
      showError(e, 'Napaka pri dodajanju brokerjev');
    }
  };

  const backfillOlder = async () => {
    if (!window.confirm('Zajamem še starejše avtohiše (pod najnižjim brokerjem, do 1)? Skener teče 07–19, ~15 s na brokerja — to traja nekaj dni.')) return;
    try {
      const res = await apiService.backfillOlderDealers();
      showOk(res.data.from ? `Zajem starejših od broker=${res.data.from} navzdol.` : (res.data.status || 'OK'));
      loadStatus();
    } catch (e) {
      showError(e, 'Napaka pri zagonu zajema');
    }
  };

  const normalizePhones = async () => {
    setNormalizing(true);
    try {
      const res = await apiService.normalizeDealerPhones();
      showOk(`SMS številke: ${res.data.zMobilno} z mobilno, ${res.data.brezMobilne} brez (spremenjenih ${res.data.spremenjene}).`);
      loadDealers();
    } catch (e) {
      showError(e, 'Napaka pri normalizaciji');
    } finally {
      setNormalizing(false);
    }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await apiService.exportDealersXlsx();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `avtohise-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      showError(null, 'Izvoz ni uspel');
    } finally {
      setExporting(false);
    }
  };

  const smsBadge = (d) => (
    <span style={{
      display: 'inline-block', marginTop: 3, padding: '1px 7px', borderRadius: 10, fontSize: 11, whiteSpace: 'nowrap',
      background: d.smsLahko ? '#e7f6e7' : '#fdecea', color: d.smsLahko ? '#1b7a1b' : '#b3261e',
    }}>{d.smsStatus || '—'}</span>
  );

  const modeLabel = (mode) => {
    if (mode === 'backfill') return 'Začetni zajem (backfill)';
    if (mode === 'scan') return 'Iskanje novih (scan)';
    return 'V mirovanju';
  };

  const renderList = (value) => (value ? String(value).split(';').filter(Boolean) : []);

  return (
    <div>
      <Navigation />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
        <h2>Avtohiše (avto.net)</h2>

        {user && user.role !== 'ADMIN' ? (
          <p style={{ color: '#d00' }}>Nimaš administratorskih pravic.</p>
        ) : forbidden ? (
          <p style={{ color: '#d00' }}>Dostop zavrnjen (potrebne so administratorske pravice).</p>
        ) : (
          <>
            {/* Scraper status + Scan trigger */}
            <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 20, background: '#fafafa' }}>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, color: '#555' }}>Stanje scraperja</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#007AFF' }}>{status ? modeLabel(status.mode) : '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#555' }}>Najnovejši broker</div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{status ? (status.newestBroker || '—') : '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#555' }}>Zadnji obdelan broker</div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{status ? (status.lastBroker || '—') : '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#555' }}>Avtohiš v bazi</div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{status ? status.totalDealers : '—'}</div>
                </div>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button onClick={exportXlsx} disabled={exporting} style={{ padding: '8px 14px' }} title="Vse avtohiše z oglasi, telefoni, SMS številko in statusom">
                    {exporting ? 'Pripravljam …' : 'Izvoz Excel'}
                  </button>
                  <button onClick={normalizePhones} disabled={normalizing} style={{ padding: '8px 14px' }} title="Iz telefonov izbere prvo slovensko mobilno številko za SMS">
                    {normalizing ? 'Normaliziram …' : 'Normaliziraj SMS številke'}
                  </button>
                  <button onClick={loadStatus} style={{ padding: '8px 14px' }}>Osveži</button>
                  <button onClick={cleanupEmpty} disabled={cleaning} style={{ padding: '8px 14px' }} title="Izbriše prazne www.Avto.net avtohiše (razen najvišje)">
                    {cleaning ? 'Čistim …' : 'Počisti prazne'}
                  </button>
                  <button
                    onClick={requestScan}
                    disabled={scanning || (status && (status.mode === 'backfill' || status.scanRequested))}
                    style={{ padding: '8px 16px', background: '#007AFF', color: 'white', border: 'none', borderRadius: 6 }}
                  >
                    {scanning ? 'Zaganjam …' : 'Scan – poišči nove'}
                  </button>
                </div>
              </div>
              {status && status.status ? (
                <div style={{ marginTop: 10, fontSize: 13, color: '#555' }}>{status.status}</div>
              ) : null}
              <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <textarea
                  value={manualText}
                  onChange={(e) => setManualText(e.target.value)}
                  rows={2}
                  placeholder="Manjka avtohiša? Prilepi avto.net povezavo (…&broker=305&…) ali broker številke, ločene z vejico"
                  style={{ flex: 1, minWidth: 320, padding: 7, fontSize: 13 }}
                />
                <button onClick={addManual} disabled={!manualText.trim()} style={{ padding: '8px 14px' }}>Dodaj v zajem</button>
                <button onClick={backfillOlder} style={{ padding: '8px 14px' }} title="Zajem avtohiš s starejšimi (nižjimi) broker številkami">Zajemi starejše</button>
              </div>
              {status ? (
                <div style={{ marginTop: 6, fontSize: 12, color: '#777' }}>
                  Najnižji broker v bazi: {status.minBroker || '—'}
                  {status.manualQueue && status.manualQueue.length ? ` · ročna vrsta: ${status.manualQueue.join(', ')}` : ''}
                  {status.backfillDone === false ? ` · zajem starejših v teku (broker ${status.backfillCursor})` : ''}
                </div>
              ) : null}
            </div>

            <DealerSmsLists reloadKey={listsReload} onChanged={() => { loadDealers(); loadSmsLists(); }} showOk={showOk} showError={showError} />

            {/* Proxy source for the dealer scraper */}
            <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 16, marginBottom: 20 }}>
              <div style={{ fontWeight: 600, marginBottom: 10 }}>Proxyji za scrapanje avtohiš</div>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="radio" name="proxySource" checked={proxySource === 'shared'} onChange={() => changeSource('shared')} />
                  Uporabi obstoječe (deljene) proxyje AvtoRadar
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="radio" name="proxySource" checked={proxySource === 'dedicated'} onChange={() => changeSource('dedicated')} />
                  Namenski proxyji samo za avtohiše
                </label>
              </div>

              {proxySource === 'dedicated' ? (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 13, color: '#555', marginBottom: 8 }}>
                    Primarno se uporabijo namenski proxyji; če vsi odpovejo, se začasno uporabijo deljeni
                    ({sharedEnabledCount} aktivnih deljenih na voljo kot rezerva).
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', borderBottom: '2px solid #ddd' }}>
                        <th style={{ padding: 8 }}>IP / host</th>
                        <th style={{ padding: 8 }}>Port</th>
                        <th style={{ padding: 8 }}>Uporabnik</th>
                        <th style={{ padding: 8 }}>Stanje</th>
                        <th style={{ padding: 8 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {dealerProxies.map((p) => (
                        <tr key={p.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: 8 }}>{p.host}</td>
                          <td style={{ padding: 8 }}>{p.port}</td>
                          <td style={{ padding: 8 }}>{p.username ? `${p.username} 🔒` : '—'}</td>
                          <td style={{ padding: 8 }}>
                            <button onClick={() => toggleProxy(p.id)} style={{ padding: '4px 10px', background: p.enabled ? '#080' : '#999', color: 'white', border: 'none', borderRadius: 4 }}>
                              {p.enabled ? 'aktiven' : 'neaktiven'}
                            </button>
                          </td>
                          <td style={{ padding: 8 }}>
                            <button onClick={() => removeProxy(p.id)} style={{ padding: '4px 10px', color: '#d00' }}>Odstrani</button>
                          </td>
                        </tr>
                      ))}
                      {dealerProxies.length === 0 ? <tr><td colSpan={5} style={{ padding: 8, color: '#888' }}>Ni namenskih proxyjev.</td></tr> : null}
                    </tbody>
                  </table>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input value={newProxyHost} onChange={(e) => setNewProxyHost(e.target.value)} placeholder="IP / host (npr. 77.111.9.70)" style={{ padding: 7, minWidth: 220 }} />
                    <input value={newProxyPort} onChange={(e) => setNewProxyPort(e.target.value)} placeholder="port" style={{ padding: 7, width: 80 }} />
                    <input value={newProxyUser} onChange={(e) => setNewProxyUser(e.target.value)} placeholder="uporabnik (če zahteva)" style={{ padding: 7, minWidth: 150 }} />
                    <input type="password" value={newProxyPass} onChange={(e) => setNewProxyPass(e.target.value)} placeholder="geslo (če zahteva)" style={{ padding: 7, minWidth: 150 }} />
                    <button onClick={addProxy} style={{ padding: '8px 16px' }}>Dodaj proxy</button>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Search */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') doSearch(); }}
                placeholder="Išči (ime / podime / naslov / telefon / broker)…"
                style={{ padding: 7, minWidth: 320 }}
              />
              <button onClick={doSearch} style={{ padding: '8px 16px' }}>Išči</button>
              {query ? <button onClick={() => { setSearch(''); setQuery(''); setPage(0); }} style={{ padding: '8px 12px' }}>Počisti</button> : null}
              <span style={{ marginLeft: 'auto', color: '#888', fontSize: 13 }}>{totalElements} avtohiš</span>
            </div>

            {/* Izbira -> dodaj v SMS listo */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12, padding: '8px 10px', background: selected.size ? '#f0f6ff' : '#fafafa', border: '1px solid #e3e3e3', borderRadius: 6, fontSize: 13 }}>
              <b>Izbrano: {selected.size}</b>
              <button onClick={() => selectPage(true)} style={{ padding: '4px 10px' }} title="Označi na tej strani vse, ki lahko dobijo SMS">Označi stran (lahko SMS)</button>
              <button onClick={() => selectPage(false)} style={{ padding: '4px 10px' }}>Označi vso stran</button>
              {selected.size ? <button onClick={() => setSelected(new Set())} style={{ padding: '4px 10px' }}>Počisti izbiro</button> : null}
              <span style={{ marginLeft: 'auto' }}>Dodaj v:</span>
              <select value={targetList} onChange={(e) => setTargetList(e.target.value)} style={{ padding: 5 }}>
                <option value="new">+ nova lista</option>
                {smsLists.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.count})</option>)}
              </select>
              {targetList === 'new' ? (
                <input value={newListName} onChange={(e) => setNewListName(e.target.value)} placeholder={defaultListName || 'Ime liste'} style={{ padding: 5, minWidth: 180 }} />
              ) : null}
              <button onClick={addToList} disabled={!selected.size} style={{ padding: '5px 14px', background: selected.size ? '#007AFF' : undefined, color: selected.size ? 'white' : undefined, border: selected.size ? 'none' : undefined, borderRadius: 5 }}>
                Dodaj v listo
              </button>
            </div>

            {loading ? <Spinner /> : dealers.length === 0 ? (
              <p>Ni avtohiš.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '2px solid #ddd', color: '#555' }}>
                    <th style={{ padding: 8 }}></th>
                    <th style={{ padding: 8 }}>Logo</th>
                    <th style={{ padding: 8 }}>Avtohiša</th>
                    <th style={{ padding: 8 }}>Naslov</th>
                    <th style={{ padding: 8 }}>Telefoni</th>
                    <th style={{ padding: 8 }}>SMS</th>
                    <th style={{ padding: 8 }}>Status</th>
                    <th style={{ padding: 8 }}>Splet / e-pošta</th>
                    <th style={{ padding: 8 }}>Registriran od</th>
                    <th style={{ padding: 8 }}>NOVO</th>
                    <th style={{ padding: 8 }} title="Oglasi na strani / št. strani">Oglasi</th>
                    <th style={{ padding: 8 }}>avto.net</th>
                    <th style={{ padding: 8 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {dealers.map((d) => (
                    <React.Fragment key={d.id}>
                    <tr style={{ borderBottom: '1px solid #eee', verticalAlign: 'top', background: selected.has(d.id) ? '#f0f6ff' : undefined }}>
                      <td style={{ padding: 8 }}>
                        <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggleSelected(d.id)} />
                      </td>
                      <td style={{ padding: 8 }}>
                        {d.logoUrl ? <img src={d.logoUrl} alt="" style={{ maxWidth: 70, maxHeight: 40 }} onError={(e) => { e.target.style.display = 'none'; }} /> : null}
                      </td>
                      <td style={{ padding: 8 }}>
                        <div style={{ fontWeight: 600 }}>{d.name || '—'}</div>
                        {d.subname ? <div style={{ color: '#777' }}>{d.subname}</div> : null}
                      </td>
                      <td style={{ padding: 8 }}>{d.address || '—'}</td>
                      <td style={{ padding: 8 }}>
                        {renderList(d.phones).map((p, i) => <div key={i}>{p}</div>)}
                        {d.phoneComment ? <div style={{ color: '#0a0', fontSize: 12 }}>{d.phoneComment}</div> : null}
                      </td>
                      <td style={{ padding: 8, whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 600 }}>{d.smsPhone || '—'}</div>
                        {smsBadge(d)}
                        {d.zadnjiSms ? <div style={{ color: '#888', fontSize: 11 }}>zadnji SMS {d.zadnjiSms}</div> : null}
                      </td>
                      <td style={{ padding: 8, whiteSpace: 'nowrap', fontSize: 12 }}>
                        <label style={{ display: 'block', cursor: 'pointer', color: d.nasaStranka ? '#1b7a1b' : undefined, fontWeight: d.nasaStranka ? 700 : 400 }}>
                          <input type="checkbox" checked={!!d.nasaStranka} onChange={(e) => setFlag(d, 'nasaStranka', e.target.checked)} /> NAŠA STRANKA
                        </label>
                        <label style={{ display: 'block', cursor: 'pointer', color: d.izkljucen ? '#b3261e' : undefined, fontWeight: d.izkljucen ? 700 : 400 }}>
                          <input type="checkbox" checked={!!d.izkljucen} onChange={(e) => setFlag(d, 'izkljucen', e.target.checked)} /> IZKLJUČI
                        </label>
                      </td>
                      <td style={{ padding: 8 }}>
                        {d.website ? <div><a href={d.website} target="_blank" rel="noreferrer">{d.website}</a></div> : null}
                        {renderList(d.emails).map((m, i) => <div key={i}><a href={`mailto:${m}`}>{m}</a></div>)}
                        {!d.website && renderList(d.emails).length === 0 ? '—' : null}
                      </td>
                      <td style={{ padding: 8 }}>{d.registeredSince || '—'}</td>
                      <td style={{ padding: 8, textAlign: 'center', fontWeight: 600 }}>{d.newAdsCount != null ? d.newAdsCount : '—'}</td>
                      <td style={{ padding: 8, textAlign: 'center' }}>{d.adsOnPage != null ? `${d.adsOnPage}/${d.totalPages != null ? d.totalPages : 1}` : '—'}</td>
                      <td style={{ padding: 8 }}>
                        <a href={d.avtonetUrl} target="_blank" rel="noreferrer">broker={d.brokerNumber}</a>
                      </td>
                      <td style={{ padding: 8, whiteSpace: 'nowrap' }}>
                        <button
                          onClick={() => toggleNotes(d)}
                          style={{
                            padding: '4px 10px',
                            marginRight: 6,
                            ...(d.notes ? { background: '#fff3e0', border: '1px solid #e67e22', color: '#b25400', fontWeight: 600 } : {}),
                          }}
                          title={d.notes ? `Ima opombo: ${String(d.notes).slice(0, 120)}${String(d.notes).length > 120 ? '…' : ''}` : 'Dodaj opombo'}
                        >
                          {expandedNotes === d.id ? 'Skrij' : (d.notes ? '✏️ Opombe' : 'Opombe')}
                        </button>
                        <button onClick={() => deleteDealer(d.id, d.name)} style={{ padding: '4px 10px', color: '#d00' }}>Briši</button>
                      </td>
                    </tr>
                    {expandedNotes === d.id ? (
                      <tr>
                        <td colSpan={13} style={{ padding: 8, background: '#fafafa' }}>
                          <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 12, color: '#555' }}>Opombe za {d.name || `broker ${d.brokerNumber}`}</div>
                          <textarea
                            value={noteDrafts[d.id] !== undefined ? noteDrafts[d.id] : (d.notes || '')}
                            maxLength={2000}
                            rows={3}
                            onChange={(e) => setNoteDrafts((n) => ({ ...n, [d.id]: e.target.value }))}
                            placeholder="Beležka (npr. po telefonskem klicu) …"
                            style={{ width: '100%', padding: 8, boxSizing: 'border-box' }}
                          />
                          <div style={{ marginTop: 6 }}>
                            <button onClick={() => saveNotes(d.id)} style={{ padding: '6px 14px' }}>Shrani opombo</button>
                            <span style={{ marginLeft: 10, color: '#007AFF', fontSize: 13 }}>{noteStatus[d.id]}</span>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}

            {/* Pagination */}
            {!loading && dealers.length > 0 ? (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} style={{ padding: '6px 14px' }}>Prejšnja</button>
                <span style={{ margin: '0 16px' }}>Stran {page + 1} od {totalPages}</span>
                <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1} style={{ padding: '6px 14px' }}>Naslednja</button>
              </div>
            ) : null}
          </>
        )}
      </div>
      <Toast message={toast && toast.message} type={toast && toast.type} onClose={() => setToast(null)} />
    </div>
  );
}

export default DealersAdminPanel;
